/**
 * Agent Runner — Phase F8
 *
 * Executes an agentic PLAN (from POST /ai/copilot/agent/plan) step-by-step
 * through the EXISTING actuation layer:
 *   navigate       → action-bridge navigate (router)
 *   fillForm       → open create form + HITL diff + RHF populate
 *   search         → copilot.service.semanticSearch
 *   findSimilar    → copilot.service.findSimilar
 *   analyze        → copilot.service.ragAnalyze
 *   suggestLinks   → copilot.service.suggestLinks
 *   generateRecord → copilot.service.generateRecord
 *   createRecord   → backend tool executor (module create endpoint), HITL-gated
 *   linkRecords    → backend tool executor (module link endpoint), HITL-gated
 *
 * `{{stepN.field}}` references in a step's args are resolved from prior step
 * outputs. Every step whose `confirm` is true is routed through the shared HITL
 * gate (the same `copilot:preview` → <HITLGate> path the sidebar uses), so the
 * user approves each write (with a diff) before it runs.
 */

import { toast } from 'sonner'
import { executeAction } from './action-bridge'
import {
  requestOpenCreate,
  isCreateSurfaceReady,
  applyFormValues,
} from './action-bridge'
import { hitlProposeValues, hitlBackendTool } from './hitl'
import { getFormSchema, type FormSchema } from './formSchemas'
import {
  resolveCreateEntryOrNull,
  getCreateEntryByFormId,
  getCreateEntryByModule,
  getCreateEntryByEntityType,
  applyCreateFieldMap,
  formSchemaToFillSchema,
} from './create-registry'
import { copilotApi, type AgentPlanStep } from '@/services/copilot.service'

// ── Step result ───────────────────────────────────────────────────────────────

export interface StepResult {
  ok: boolean
  /** Value bound to this step's number for later `{{stepN.field}}` refs. */
  output?: Record<string, unknown>
  detail?: string
  /** True when the user cancelled a HITL gate — halts the plan without error. */
  cancelled?: boolean
}

export type StepPhase = 'running' | 'done' | 'failed' | 'skipped'

export interface RunPlanContext {
  module?: string
  pageContext?: string
  /** Companion autonomy mode (subsystem H) — decides tool-registry gating. */
  mode?: 'assistant' | 'agent'
  /** Current detail entity (subsystem B/N) — lets registry tools derive inputs. */
  entity?: import('@/stores/useCopilotEntityStore').CopilotEntityContext | null
  /** File the user dropped on the Companion (CV perception chain). */
  file?: File | null
  /**
   * Optional sink for rich tool cards (ML/CV/query results) produced by
   * registry-resolved steps, so the Companion window can render them in-stream.
   */
  onCard?: (step: number, card: import('./tools/types').ToolCard) => void
}

// ── Template resolution: {{stepN.field}} ──────────────────────────────────────

const TEMPLATE_RE = /\{\{\s*step(\d+)\.([^}]+?)\s*\}\}/g

function readPath(obj: unknown, dotPath: string): unknown {
  return dotPath.split('.').reduce<unknown>((acc, key) => {
    if (acc == null) return undefined
    // Support array index access like results.0.entity_id
    return (acc as Record<string, unknown>)[key]
  }, obj)
}

/** Resolve `{{stepN.field}}` refs inside any value (string / object / array). */
export function resolveTemplates(
  value: unknown,
  outputs: Record<number, Record<string, unknown>>,
): unknown {
  if (typeof value === 'string') {
    // Whole-string single placeholder → return the raw (possibly non-string) value.
    const whole = /^\{\{\s*step(\d+)\.([^}]+?)\s*\}\}$/.exec(value.trim())
    if (whole) {
      const resolved = readPath(outputs[parseInt(whole[1], 10)], whole[2])
      return resolved ?? value
    }
    // Otherwise interpolate every placeholder into the string.
    return value.replace(TEMPLATE_RE, (m, stepStr, path) => {
      const resolved = readPath(outputs[parseInt(stepStr, 10)], path)
      return resolved == null ? m : String(resolved)
    })
  }
  if (Array.isArray(value)) {
    return value.map((v) => resolveTemplates(v, outputs))
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = resolveTemplates(v, outputs)
    }
    return out
  }
  return value
}

function arg<T = unknown>(args: Record<string, unknown>, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (args[k] !== undefined && args[k] !== null) return args[k] as T
  }
  return undefined
}

// ── Op classification (read vs write) ─────────────────────────────────────────
//
// READ / preview ops are safe to auto-run AND safe to FAIL: a read error (e.g.
// findSimilar 404 because the entity type isn't indexed) must be skipped so the
// plan CONTINUES to the next (confirm-gated) write step — never halted (#D3).
const READ_OPS = new Set<AgentPlanStep['op']>([
  'navigate',
  'search',
  'findSimilar',
  'analyze',
  'suggestLinks',
  'generateRecord',
])
const WRITE_OPS = new Set<AgentPlanStep['op']>(['fillForm', 'createRecord', 'linkRecords'])

export function isReadOp(op: AgentPlanStep['op']): boolean {
  if (READ_OPS.has(op)) return true
  // Registry-resolved read tools (queryData, detectAnomalies, forecast, analyze…)
  // must also be treated as read so a failed advisory step SKIPS + the plan
  // continues to its (confirm-gated) write steps (#D3) — never halts.
  const t = registryModeOf(op)
  return t === 'read'
}
export function isWriteOp(op: AgentPlanStep['op']): boolean {
  if (WRITE_OPS.has(op)) return true
  const t = registryModeOf(op)
  return t === 'write' || t === 'destructive'
}

/**
 * Synchronously read a registry tool's side-effect class WITHOUT triggering the
 * dynamic import chain in the hot path. Populated once (best-effort) so the
 * read/write classification above is cheap; unknown ops return undefined and are
 * treated conservatively (as writes → halt on failure).
 */
let _toolModeCache: Record<string, 'read' | 'write' | 'destructive'> | null = null
function registryModeOf(op: string): 'read' | 'write' | 'destructive' | undefined {
  if (_toolModeCache) return _toolModeCache[op]
  return undefined
}
/** Called by the registry (or the Companion mount) to prime the mode cache. */
export function primeToolModeCache(map: Record<string, 'read' | 'write' | 'destructive'>): void {
  _toolModeCache = map
}

// ── Unresolved {{stepN.field}} detection (#AG6) ────────────────────────────────
//
// After resolveTemplates, a `{{stepN.id}}` that referenced a step which produced
// no `id` (e.g. a fillForm that filled but persisted nothing) stays as a LITERAL.
// We must never POST that literal to the backend — detect + surface instead.
function collectUnresolved(value: unknown, acc: string[] = []): string[] {
  if (typeof value === 'string') {
    const m = value.match(/\{\{\s*step\d+\.[^}]+?\s*\}\}/g)
    if (m) acc.push(...m)
  } else if (Array.isArray(value)) {
    value.forEach((v) => collectUnresolved(v, acc))
  } else if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((v) => collectUnresolved(v, acc))
  }
  return acc
}

// ── Required / range normalization of AI-filled values (#AG4 + #AG7) ───────────
//
// The planner (or fill-form) can (a) leave a required numeric field null so the
// zod-guarded form silently refuses to submit, or (b) emit an out-of-range number.
// We clamp numerics to [min,max] and default any MISSING required numeric field
// to the mid-point of its range, returning the labels of fields the user must
// still complete so the caller can flag them (never a silent validation failure).
export interface NormalizedFill {
  values: Record<string, unknown>
  missingRequired: string[]
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

export function normalizeFillValues(
  schema: FormSchema | undefined,
  values: Record<string, unknown>,
): NormalizedFill {
  if (!schema) return { values, missingRequired: [] }
  const out: Record<string, unknown> = { ...values }
  const missingRequired: string[] = []

  for (const f of schema.fields) {
    const present = out[f.name] !== undefined && out[f.name] !== null && out[f.name] !== ''

    if (f.type === 'number') {
      const n = toNum(out[f.name])
      const hasRange = f.min !== undefined || f.max !== undefined
      if (n !== null) {
        // #AG7 — clamp to the declared range on apply.
        let clamped = n
        if (f.min !== undefined) clamped = Math.max(f.min, clamped)
        if (f.max !== undefined) clamped = Math.min(f.max, clamped)
        out[f.name] = clamped
      } else if (f.required && hasRange) {
        // #AG4 — required numeric left null → default to the range mid-point so
        // the form can be submitted; flag it so the user reviews the guess.
        const lo = f.min ?? 1
        const hi = f.max ?? lo
        out[f.name] = Math.round((lo + hi) / 2)
        missingRequired.push(f.label)
      } else if (f.required) {
        missingRequired.push(f.label)
      }
      continue
    }

    if (f.required && !present) missingRequired.push(f.label)
  }

  return { values: out, missingRequired }
}

// ── Human-readable step label ─────────────────────────────────────────────────

export function describeStep(step: AgentPlanStep): string {
  if (step.label) return step.label
  const a = step.args ?? {}
  switch (step.op) {
    case 'navigate':
      return `Naviguer vers ${arg<string>(a, 'to', 'route', 'path') ?? '…'}`
    case 'fillForm':
      return `Remplir le formulaire « ${arg<string>(a, 'form_id', 'formId') ?? '…'} »`
    case 'search':
      return `Rechercher « ${arg<string>(a, 'query', 'q') ?? '…'} »`
    case 'findSimilar':
      return `Trouver des éléments similaires (${arg<string>(a, 'entity_type') ?? '…'})`
    case 'analyze':
      return `Analyser : ${arg<string>(a, 'question', 'prompt') ?? '…'}`
    case 'suggestLinks':
      return `Suggérer des liens vers ${arg<string>(a, 'target_type') ?? '…'}`
    case 'generateRecord':
      return `Générer un brouillon (${arg<string>(a, 'entity_type') ?? '…'})`
    case 'createRecord':
      return `Créer l'enregistrement (${arg<string>(a, 'entity_type', 'module') ?? '…'})`
    case 'linkRecords':
      return `Lier les enregistrements`
    default:
      return `Action « ${step.op} »`
  }
}

// ── Individual op executors ────────────────────────────────────────────────────

/**
 * Open a create form and wait until it mounts + registers.
 *
 * The host page attaches its `copilot:openCreate` listener only once it mounts
 * (and lazily-loaded routes mount a beat after navigation). A single dispatch
 * therefore races the mount, so we (re)dispatch on a short interval until the
 * form registers or we time out — robust to the navigate→lazy-mount delay.
 */
export async function openCreateForm(formId: string, timeoutMs = 9000): Promise<boolean> {
  const started = Date.now()
  return new Promise((resolve) => {
    const tick = () => {
      // Always (re)ask the host page to open its create dialog — robust to the
      // navigate→lazy-mount delay, AND required for applier-based (non-RHF)
      // surfaces (policy/control/obligation) whose prefill applier registers on
      // page mount, before the dialog is opened, so we still must dispatch open.
      requestOpenCreate(formId)
      if (isCreateSurfaceReady(formId)) return resolve(true)
      if (Date.now() - started > timeoutMs) return resolve(false)
      window.setTimeout(tick, 400)
    }
    tick()
  })
}

/**
 * Apply confirmed values into a create form AFTER the HITL diff.
 *
 * The HITL gate is a separate portal, so confirming it counts as a pointer-down
 * "outside" the Radix create dialog and closes it. We therefore let that close
 * settle, reopen the form fresh, then populate the remounted RHF instance.
 */
async function applyConfirmedValues(
  formId: string,
  values: Record<string, unknown>,
): Promise<boolean> {
  // Let the HITL-confirm outside-click finish closing the dialog…
  await new Promise((r) => setTimeout(r, 450))
  // …then reopen it and wait for the surface to remount + become fillable.
  const ready = await openCreateForm(formId, 7000)
  if (!ready) return false
  await new Promise((r) => setTimeout(r, 250))
  // applyFormValues handles BOTH an RHF instance (setValue) and a page-registered
  // prefill applier (non-RHF useState dialogs), so any module's create surface
  // gets populated — not only the handful backed by React Hook Form.
  return applyFormValues(formId, values)
}

async function ensureFormReady(formId: string, module?: string): Promise<boolean> {
  if (isCreateSurfaceReady(formId)) return true
  const entry = getCreateEntryByFormId(formId) ?? getCreateEntryByModule(module)
  if (entry && typeof window !== 'undefined' && window.location.pathname !== entry.route) {
    await executeAction({ op: 'navigate', to: entry.route })
  }
  return openCreateForm(formId, 9000)
}

async function runFillForm(
  step: AgentPlanStep,
  args: Record<string, unknown>,
  ctx: RunPlanContext,
): Promise<StepResult> {
  const formId = arg<string>(args, 'form_id', 'formId') ?? ''
  if (!formId) return { ok: false, detail: 'fillForm sans form_id' }
  const schema = getFormSchema(formId)

  const mounted = await ensureFormReady(formId, ctx.module)

  // Resolve the values to propose: explicit values, or an LLM fill from a prompt.
  let values = (arg<Record<string, unknown>>(args, 'values', 'fields') ?? {}) as Record<string, unknown>
  const prompt = arg<string>(args, 'prompt', 'description')
  if (Object.keys(values).length === 0 && prompt && schema) {
    try {
      const filled = await copilotApi.fillForm({
        form_schema: formSchemaToFillSchema(schema),
        prompt,
        module: ctx.module,
      })
      values = filled.fields ?? {}
    } catch {
      /* fall through — empty diff still lets the user type */
    }
  }

  // Clamp numerics to range + backfill required numeric fields (#AG4 + #AG7) so
  // the pre-filled form can actually be submitted (no silent zod failure).
  const normalized = normalizeFillValues(schema, values)
  values = normalized.values
  if (normalized.missingRequired.length > 0) {
    toast.message('Champs à vérifier', {
      description: `Valeurs par défaut proposées pour : ${normalized.missingRequired.join(', ')}. Ajustez-les avant d’enregistrer.`,
    })
  }

  // HITL diff (unless the step is explicitly non-confirming).
  let finalValues: Record<string, unknown> | null = values
  if (step.confirm !== false) {
    finalValues = await hitlProposeValues(formId, values)
    if (finalValues === null) return { ok: false, cancelled: true, detail: 'Annulé' }
  }

  if (mounted) {
    const applied =
      step.confirm !== false
        ? await applyConfirmedValues(formId, finalValues)
        : await applyFormValues(formId, finalValues)
    return {
      ok: true,
      detail: applied
        ? `Formulaire « ${formId} » rempli (${Object.keys(finalValues).length} champ(s))`
        : `Valeurs confirmées pour « ${formId} »`,
      output: { ...finalValues, values: finalValues, formId },
    }
  }
  // Form never mounted — still return the values so a later createRecord can use them.
  return {
    ok: true,
    detail: `Valeurs prêtes pour « ${formId} » (formulaire non ouvert)`,
    output: { ...finalValues, values: finalValues, formId },
  }
}

async function runBackendWrite(
  step: AgentPlanStep,
  args: Record<string, unknown>,
  ctx: RunPlanContext,
  kind: 'createRecord' | 'linkRecords',
): Promise<StepResult> {
  // #AG6 — refuse to POST an unresolved `{{stepN.field}}` (e.g. a `{{step2.id}}`
  // that referenced a fillForm step which persisted nothing → produced no id).
  // Surfacing this is far better than silently sending the literal to the backend.
  const unresolved = collectUnresolved(args)
  if (unresolved.length > 0) {
    return {
      ok: false,
      detail:
        `Référence non résolue ${unresolved[0]} — l’étape citée n’a pas produit d’identifiant. ` +
        `Insérez une étape « createRecord » (qui persiste et renvoie un id) avant de lier.`,
    }
  }

  // Endpoint: explicit override wins, else resolve from the create registry by
  // entity_type FIRST (so 'security_control' → control, never the risk fallback),
  // then module, then a keyword resolve — but NEVER silently default to risk (#AG3).
  let path = arg<string>(args, 'path', 'endpoint')
  const entity = arg<string>(args, 'entity_type', 'module')
  const entry =
    getCreateEntryByEntityType(entity) ??
    getCreateEntryByModule(entity) ??
    getCreateEntryByModule(ctx.module) ??
    (entity ? resolveCreateEntryOrNull(entity, ctx.module) ?? undefined : undefined)

  // Field-bag: createRecord nests its values under one of these keys. linkRecords
  // (the planner emits {source_ref, target_ref, link_type} at the TOP level) rarely
  // has a nested bag, so we fold the flat args in below.
  let body =
    (arg<Record<string, unknown>>(args, 'fields', 'values', 'body', 'data', 'payload') as
      | Record<string, unknown>
      | undefined) ?? {}

  if (kind === 'linkRecords') {
    // The durable typed-link endpoint is
    //   POST /<module>/<collection>/{sourceId}/links   (e.g. /risk-pro/risks/{risk_id}/links)
    // → the SOURCE id belongs in the PATH (not the body), and the body carries
    // {link_type, target_ref|target_id}. Map the planner's flat
    // {source_ref, target_ref, link_type} onto that contract.
    // (Previously we POSTed to `${createPath}/links` with NO id → 405 Method Not Allowed.)
    const sourceRef =
      arg<string>(
        args, 'source_ref', 'source_id', 'sourceRef', 'sourceId',
        'risk_id', 'riskId', 'entity_id', 'entityId', 'id',
      ) ??
      (body.source_ref as string | undefined) ??
      (body.id as string | undefined)
    if (!path && entry && sourceRef) {
      path = `${entry.createPath}/${encodeURIComponent(String(sourceRef))}/links`
    }
    if (!path) return { ok: false, detail: 'linkRecords : source du lien introuvable' }

    const linkType = arg<string>(args, 'link_type', 'linkType', 'type', 'target_type', 'targetType')
    const targetRef = arg<string>(args, 'target_ref', 'targetRef', 'target_code', 'targetCode', 'target')
    const targetId = arg<string>(args, 'target_id', 'targetId')
    body = {
      ...body,
      link_type: String(linkType ?? (body.link_type as string) ?? 'CONTROL').toUpperCase(),
      ...(targetRef ? { target_ref: targetRef } : {}),
      ...(targetId ? { target_id: targetId } : {}),
    }
    // Routing keys don't belong in the persisted link row.
    delete body.source_ref
    delete body.source_id
  } else {
    if (!path && entry) path = entry.createPath
    if (!path) {
      return {
        ok: false,
        detail: entity
          ? `Type « ${entity} » non reconnu — impossible de créer l’enregistrement. Précisez le type (risque, politique, contrôle, obligation, incident, fournisseur…).`
          : `${kind} : endpoint introuvable`,
      }
    }
    // Map planner/schema field names onto the backend contract + inject any
    // constant discriminators (e.g. type:'policy') so nothing is dropped (#AG3).
    body = applyCreateFieldMap(entry, body)
  }

  // Force a write-class side_effect so this POST always routes through the HITL
  // gate (never silently classified 'read' from a stray planner value).
  const sideEffect: 'write' | 'destructive' | 'outward' =
    step.side_effect === 'destructive' || step.side_effect === 'outward'
      ? step.side_effect
      : 'write'

  const call = {
    capability_code: `${entity ?? 'record'}.${kind}`,
    method: 'POST',
    path,
    body,
    side_effect: sideEffect,
    description: describeStep(step),
  }

  // HITL-gated (default). Executes the network call only after confirmation.
  const result = await hitlBackendTool(call, { system: `POST ${path}` })
  if (result === null) return { ok: false, cancelled: true, detail: 'Annulé' }
  if (!result.ok) {
    return { ok: false, detail: `Échec ${result.status} : ${result.error ?? 'erreur'}` }
  }
  const data = (result.data ?? {}) as Record<string, unknown>

  // draft_review persistence policy → the record was written with a draft status;
  // register it in the /ai-approvals HITL queue so a human promotes or discards it.
  // Only for createRecord (a new record) — linkRecords never enqueues. Best-effort:
  // a missing/failed enqueue must never fail the (already-succeeded) create.
  if (kind === 'createRecord') {
    try {
      const { fetchPersistencePolicy, enqueueDraftApproval } = await import('@/services/ai-hub.service')
      const policy = await fetchPersistencePolicy()
      if (policy.mode === 'draft_review') {
        const title =
          (body.title as string | undefined) ??
          (body.name as string | undefined) ??
          (body.label as string | undefined) ??
          describeStep(step)
        await enqueueDraftApproval({
          source: 'copilot',
          entity_type: entity ?? entry?.entityType,
          entity_id: data.id ? String(data.id) : undefined,
          proposed_action: describeStep(step),
          payload: { title, ...body },
        })
      }
    } catch {
      /* enqueue is advisory — never break the create */
    }
  }

  return {
    ok: true,
    detail: `${kind === 'linkRecords' ? 'Liens créés' : 'Enregistrement créé'} (${result.status})${data.id ? ` · id=${data.id}` : ''}`,
    output: { id: data.id, ...data },
  }
}

// ── One step ───────────────────────────────────────────────────────────────────

export async function runPlanStep(
  step: AgentPlanStep,
  outputs: Record<number, Record<string, unknown>>,
  ctx: RunPlanContext,
): Promise<StepResult> {
  const args = (resolveTemplates(step.args ?? {}, outputs) as Record<string, unknown>) ?? {}

  switch (step.op) {
    case 'navigate': {
      const to = arg<string>(args, 'to', 'route', 'path')
      if (!to) return { ok: false, detail: 'navigate sans cible' }
      const res = await executeAction({ op: 'navigate', to })
      return { ok: res.ok, detail: res.detail, output: { to } }
    }

    case 'fillForm':
      return runFillForm(step, args, ctx)

    case 'search': {
      const query = arg<string>(args, 'query', 'q', 'text') ?? ''
      const types = arg<string[]>(args, 'types', 'entity_types')
      const limit = arg<number>(args, 'limit')
      const results = await copilotApi.semanticSearch(query, { types, limit })
      return {
        ok: true,
        detail: `${results.length} résultat(s) pour « ${query} »`,
        output: { results, query, top: results[0] ?? null },
      }
    }

    case 'findSimilar': {
      const entityType = arg<string>(args, 'entity_type', 'type') ?? ''
      const entityId = arg<string>(args, 'entity_id', 'id') ?? ''
      const limit = arg<number>(args, 'limit')
      const results = await copilotApi.findSimilar(entityType, entityId, limit ?? 8)
      return { ok: true, detail: `${results.length} similaire(s)`, output: { results } }
    }

    case 'analyze': {
      const question = arg<string>(args, 'question', 'prompt', 'text') ?? ''
      const res = await copilotApi.ragAnalyze({
        question,
        types: arg<string[]>(args, 'types'),
        entity_type: arg<string>(args, 'entity_type'),
        entity_id: arg<string>(args, 'entity_id'),
      })
      return {
        ok: true,
        detail: res.grounded ? 'Analyse fondée sur la base de connaissances' : 'Analyse générée',
        output: { answer: res.answer, citations: res.citations },
      }
    }

    case 'suggestLinks': {
      const res = await copilotApi.suggestLinks({
        entity_type: arg<string>(args, 'entity_type', 'source_type') ?? '',
        entity_id: arg<string>(args, 'entity_id', 'source_id') ?? '',
        target_type: arg<string>(args, 'target_type') ?? 'control',
        module: ctx.module,
        limit: arg<number>(args, 'limit'),
        with_rationale: true,
      })
      return {
        ok: true,
        detail: `${res.suggestions.length} lien(s) suggéré(s)`,
        output: { suggestions: res.suggestions },
      }
    }

    case 'generateRecord': {
      const res = await copilotApi.generateRecord({
        entity_type: arg<string>(args, 'entity_type') ?? 'record',
        module: ctx.module,
        prompt: arg<string>(args, 'prompt', 'description', 'goal') ?? '',
        ground_with_rag: true,
      })
      return {
        ok: true,
        detail: res.grounded ? 'Brouillon généré (fondé RAG)' : 'Brouillon généré',
        output: { ...res.fields, fields: res.fields, body_markdown: res.body_markdown },
      }
    }

    case 'createRecord':
      return runBackendWrite(step, args, ctx, 'createRecord')

    case 'linkRecords':
      return runBackendWrite(step, args, ctx, 'linkRecords')

    default:
      // Unknown-to-the-runner op → resolve it through the unified tool registry
      // (subsystem C): navigate/openForm/queryData/recallTasks/notify/startWorkflow/
      // stopWorkflow/analyzeCurrentPage + all ML/CV tools. Keeps the same
      // {{stepN.field}} chaining (args already resolved above) and surfaces any
      // rich result card back to the window via ctx.onCard.
      return runRegistryStep(step, args, ctx)
  }
}

/** Execute a plan step by resolving its op through the CompanionToolRegistry. */
async function runRegistryStep(
  step: AgentPlanStep,
  args: Record<string, unknown>,
  ctx: RunPlanContext,
): Promise<StepResult> {
  const { resolveTool, dispatchTool } = await import('./tool-registry')
  const tool = resolveTool(step.op)
  if (!tool) return { ok: false, detail: `Opération non supportée : ${step.op}` }

  const toolCtx = {
    mode: ctx.mode ?? 'agent',
    module: ctx.module,
    pageContext: ctx.pageContext,
    route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    entity: ctx.entity ?? null,
    file: ctx.file ?? null,
  }
  const res = await dispatchTool(step.op, args, toolCtx)
  if (res.card && ctx.onCard) {
    try {
      ctx.onCard(step.step, res.card)
    } catch {
      /* card sink must never break the plan */
    }
  }
  return {
    ok: res.ok,
    detail: res.detail,
    cancelled: res.cancelled,
    output: res.output ?? {},
  }
}

// ── Whole plan ─────────────────────────────────────────────────────────────────

export interface RunPlanCallbacks {
  onStep: (stepNumber: number, phase: StepPhase, detail?: string) => void
}

/**
 * Execute an ordered plan.
 *
 * READ ops that fail (e.g. findSimilar 404 on a non-indexed type, an empty
 * search, a RAG timeout) are marked SKIPPED and the plan CONTINUES — a doomed
 * read must never abort the write steps that follow (#D3). Only a failed WRITE
 * step, or a user-cancelled HITL gate, halts the plan (returning `failedAt` so
 * the caller can mark the remaining steps skipped).
 */
export async function runPlan(
  plan: AgentPlanStep[],
  ctx: RunPlanContext,
  cb: RunPlanCallbacks,
): Promise<{ outputs: Record<number, Record<string, unknown>>; failedAt: number | null }> {
  const outputs: Record<number, Record<string, unknown>> = {}
  const ordered = [...plan].sort((a, b) => a.step - b.step)

  for (const step of ordered) {
    cb.onStep(step.step, 'running')
    const readOp = isReadOp(step.op)
    try {
      const res = await runPlanStep(step, outputs, ctx)
      if (res.cancelled) {
        // User declined a HITL gate → intentional stop.
        cb.onStep(step.step, 'failed', res.detail ?? 'Annulé par l’utilisateur')
        return { outputs, failedAt: step.step }
      }
      if (res.ok) {
        outputs[step.step] = res.output ?? {}
        cb.onStep(step.step, 'done', res.detail)
      } else if (readOp) {
        // Failed read → skip + continue (never halts the plan).
        outputs[step.step] = {}
        cb.onStep(step.step, 'skipped', res.detail)
      } else {
        // Failed write → halt.
        cb.onStep(step.step, 'failed', res.detail)
        return { outputs, failedAt: step.step }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur inconnue'
      if (readOp) {
        outputs[step.step] = {}
        cb.onStep(step.step, 'skipped', msg)
      } else {
        cb.onStep(step.step, 'failed', msg)
        return { outputs, failedAt: step.step }
      }
    }
  }
  return { outputs, failedAt: null }
}

// ── Smart-create: openAndFillForm(module, prompt) ──────────────────────────────

/**
 * Resolve the target module's create route, navigate + open the create form,
 * pre-fill it from a natural-language prompt via /ai/copilot/fill-form, route
 * the proposal through the HITL diff, and apply the confirmed values into the
 * mounted form for the user to review + submit.
 *
 * Reusable by the command palette ("Créer avec l'IA…") and the sidebar
 * quick-action.
 */
export async function openAndFillForm(module: string | undefined, prompt: string): Promise<void> {
  // Resolve the intent to a concrete create surface — but do NOT silently fall
  // back to the RISK form when the intent is unclear (#AG2/#AG3). Tell the user.
  const entry = resolveCreateEntryOrNull(prompt, module)
  if (!entry) {
    toast.message('Type d’enregistrement non reconnu', {
      description:
        'Précisez ce que vous voulez créer (ex : « créer une politique de télétravail », ' +
        '« créer un risque cloud », « créer un contrôle », « créer une obligation »…).',
    })
    return
  }
  const schema = getFormSchema(entry.formId)

  // 1) Navigate to the page that hosts the create form.
  await executeAction({ op: 'navigate', to: entry.route })

  // 2) Open its create dialog so the user sees the form while the LLM drafts
  //    (retry-dispatches the open signal to survive the lazy-route mount delay).
  await openCreateForm(entry.formId, 9000)

  if (!schema) {
    toast.error(`Aucun schéma de formulaire pour « ${entry.formId} ».`)
    return
  }

  // 3) Pre-fill from the prompt (fill-form; fall back to a full generate-record).
  let fields: Record<string, unknown> = {}
  try {
    const filled = await copilotApi.fillForm({
      form_schema: formSchemaToFillSchema(schema),
      prompt,
      module: entry.module,
    })
    fields = filled.fields ?? {}
    if (Object.keys(fields).length === 0) {
      const gen = await copilotApi.generateRecord({
        entity_type: entry.entityType,
        module: entry.module,
        prompt,
        ground_with_rag: true,
      })
      fields = gen.fields ?? {}
    }
  } catch {
    toast.error('Le pré-remplissage IA a échoué. Vérifiez vos crédits ou réessayez.')
    return
  }

  if (Object.keys(fields).length === 0) {
    toast.message('Formulaire ouvert', {
      description: "L'IA n'a pas pu déduire de valeurs — complétez le formulaire manuellement.",
    })
    return
  }

  // 3b) Clamp numerics + backfill required numeric fields (#AG4 + #AG7) so the
  //     pre-filled form is actually submittable (no silent zod failure), and
  //     tell the user which auto-filled fields to double-check.
  const normalized = normalizeFillValues(schema, fields)
  fields = normalized.values
  if (normalized.missingRequired.length > 0) {
    toast.message('Champs requis à vérifier', {
      description: `Valeurs par défaut proposées pour : ${normalized.missingRequired.join(', ')}. Ajustez-les avant d’enregistrer.`,
    })
  }

  // 4) HITL diff → confirmed values.
  const confirmed = await hitlProposeValues(entry.formId, fields)
  if (confirmed === null) {
    toast.message('Création IA annulée.')
    return
  }

  // 5) Apply the confirmed values (reopens the dialog the HITL-confirm closed).
  const applied = await applyConfirmedValues(entry.formId, confirmed)
  if (applied) {
    toast.success(`${entry.title} pré-rempli par l'IA — vérifiez puis enregistrez.`)
  } else {
    toast.message(`${entry.title} : valeurs prêtes`, {
      description: "Ouvrez le formulaire de création pour appliquer les valeurs proposées.",
    })
  }
}

// ── runGoal — the Agent-mode runtime orchestrator (subsystem D) ────────────────
//
// Higher-level entry point the Companion Agent panel calls with a bare GOAL. It:
//   1) plans the goal (F8 server-authoritative planner, tools from the registry),
//   2) DECIDES the runtime (subsystem D): a complex/cross-module plan (>3 steps
//      or ≥2 modules) routes to the durable MAS backend; a simple plan runs
//      in-browser via runPlan (fast, HITL-gated),
//   3) for MAS: creates + starts the plan and subscribes to live progress,
//   4) for in-browser: runs the plan step-by-step (registry-resolved),
//   5) surfaces rich ML/CV/query cards to the window via `onCard`.
//
// Falls back to in-browser when MAS is unavailable so the goal always executes.

import type { ToolCard } from './tools/types'

export type GoalRuntime = 'browser' | 'mas'

export interface RunGoalCallbacks extends RunPlanCallbacks {
  /** Rich result card produced by a step (ML/CV/query/MAS). */
  onCard?: (step: number, card: ToolCard) => void
  /** Live MAS progress ticks (only when routed to MAS). */
  onMasProgress?: (p: import('@/services/ml-tools.service').MasProgress) => void
  /** Which runtime was chosen, emitted once the decision is made. */
  onRuntime?: (runtime: GoalRuntime, planId?: string) => void
}

export interface RunGoalResult {
  runtime: GoalRuntime
  /** MAS plan id when routed to MAS. */
  planId?: string
  /** The plan that was produced (both runtimes). */
  plan: AgentPlanStep[]
  assistantMessage: string
  grounded: boolean
  model?: string
  /** In-browser: the step that failed (null when clean). MAS: null. */
  failedAt: number | null
  /** In-browser step outputs (empty for MAS). */
  outputs: Record<number, Record<string, unknown>>
}

/**
 * Route + execute an ALREADY-PLANNED goal (no re-planning). `mode` decides HITL
 * policy; `entity` lets registry tools (ML/CV) derive inputs from the viewed
 * record. A complex/cross-module plan in Agent mode routes to the durable MAS
 * backend; a simple plan runs in-browser step-by-step. Falls back to in-browser
 * when MAS is unavailable so the plan always executes.
 */
export async function runPlanRouted(
  goal: string,
  plan: AgentPlanStep[],
  ctx: RunPlanContext,
  cb: RunGoalCallbacks,
): Promise<RunGoalResult> {
  const router = await import('./runtime-router')

  // Decide the runtime. Agent mode only routes to MAS; assistant mode always
  // runs in-browser (fast, every write HITL-gated).
  const wantsMas =
    ctx.mode === 'agent' &&
    (router.decidePlanRuntime(plan, ctx.module) === 'mas' || router.createMasPlanForGoal(goal))

  if (wantsMas) {
    const launch = await router.launchMasPlan({
      goal,
      domain: ctx.module,
      autonomy: ctx.mode ?? 'agent',
      context: ctx.entity ? { entity: { type: ctx.entity.entityType, id: ctx.entity.entityId } } : undefined,
    })
    if (launch.ok && launch.planId) {
      cb.onRuntime?.('mas', launch.planId)
      // Subscribe to live progress; the panel renders it in the window. Runs
      // detached so the caller returns immediately (the plan runs server-side).
      if (cb.onMasProgress) {
        void router.subscribeMasProgress(launch.planId, cb.onMasProgress)
      }
      return { runtime: 'mas', planId: launch.planId, plan, assistantMessage: '', grounded: false, failedAt: null, outputs: {} }
    }
    // MAS unavailable → fall back to in-browser so the plan still executes.
  }

  cb.onRuntime?.('browser')
  const runCtx: RunPlanContext = { ...ctx, onCard: cb.onCard ?? ctx.onCard }
  const { outputs, failedAt } = await runPlan(plan, runCtx, { onStep: cb.onStep })
  return { runtime: 'browser', plan, assistantMessage: '', grounded: false, failedAt, outputs }
}

/**
 * Plan + route + execute a natural-language goal end-to-end (one-shot). Plans
 * the goal, then delegates to `runPlanRouted`. Used by surfaces that hand a bare
 * goal (chat quick-action) rather than a pre-rendered plan.
 */
export async function runGoal(
  goal: string,
  ctx: RunPlanContext,
  cb: RunGoalCallbacks,
): Promise<RunGoalResult> {
  const [{ copilotApi }, { buildAvailableForms }, registry] = await Promise.all([
    import('@/services/copilot.service'),
    import('./create-registry'),
    import('./tool-registry'),
  ])
  const planned = await copilotApi.agentPlan({
    goal,
    module: ctx.module,
    page_context: ctx.pageContext,
    available_forms: buildAvailableForms(ctx.module),
    available_tools: registry.listToolNames(),
    max_steps: 8,
  })
  const result = await runPlanRouted(goal, planned.plan ?? [], ctx, cb)
  return { ...result, assistantMessage: planned.assistant_message ?? '', grounded: planned.grounded ?? false, model: planned.model }
}
